import { useCallback, useMemo } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { create } from 'zustand'
import { effectiveDrillSpaceId, resolveRouteSpaceId } from './spaceSidebarUtils'
import { useProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'

interface SpaceDrillState {
  /** A choice made in the sidebar, remembered against the route's space at the time. */
  manual: { spaceId: string | null; routeSpaceId: string | null } | null
  setManual: (manual: SpaceDrillState['manual']) => void
}

/** Not persisted: a reload lands wherever the route is. */
const useSpaceDrillStore = create<SpaceDrillState>()((set) => ({
  manual: null,
  setManual: (manual) => set({ manual }),
}))

/**
 * Which space the sidebar is drilled into (design 21 C).
 *
 * The route decides by default: anywhere inside a space or one of its
 * projects shows that space. "All spaces" and clicking a space row only
 * change the sidebar — they never navigate — so browsing the tree doesn't
 * throw away the page you're reading. Moving to another space's route
 * resets the choice and the route wins again.
 */
export function useSpaceDrill() {
  const workspaceId = useLoginStore((s) => s.workspaceId)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { projects } = useProjects()
  const manual = useSpaceDrillStore((s) => s.manual)
  const setManual = useSpaceDrillStore((s) => s.setManual)

  const scoped = useMemo(
    () => projects.filter((p) => !workspaceId || p.envId === workspaceId),
    [projects, workspaceId],
  )
  const routeSpaceId = useMemo(() => resolveRouteSpaceId(pathname, scoped), [pathname, scoped])
  const drilledId = effectiveDrillSpaceId(routeSpaceId, manual)
  const drilledSpace = useMemo(
    () => (drilledId ? scoped.find((p) => p.id === drilledId && !p.isDeleted && !p.parentProjectId) ?? null : null),
    [drilledId, scoped],
  )

  const drillInto = useCallback(
    (spaceId: string | null) => setManual({ spaceId, routeSpaceId }),
    [routeSpaceId, setManual],
  )

  return { drilledSpace, routeSpaceId, drillInto }
}
